import React from 'react';
import { isBlank, isNullOrUndefined } from '@utils/utils';
import type { FilterSort } from '@hooks/useFilter/Filter.tsx';

export enum FilterType {
  InputText = 'InputText',
  MultiSelect = 'MultiSelect',
  MultiSelectLazy = 'MultiSelectLazy',
  Dropdown = 'Dropdown',
  Calendar = 'Calendar',
  Checkbox = 'Checkbox',
  /** Two-level checkbox tree (e.g. locations → ramps). See {@link FilterConfig.treeOptions}. */
  TreeMultiSelect = 'TreeMultiSelect',
  /**
   * Single-select whose options are windowed by {@link FilterConfig.onLazyLoad}. PrimeReact skips its
   * client-side filtering while lazy and passes the typed text as `event.filter`, so the panel's search
   * box must be answered server-side. Options never hold the full set, so the owner is responsible for
   * keeping the *selected* option present — otherwise the chip falls back to showing the raw value.
   */
  DropdownLazy = 'DropdownLazy',
}

export interface OptionType {
  label: string;
  value: string;
  active: boolean;
  /** Optional node (e.g. a color swatch, country flag or transport icon) rendered before the label. */
  icon?: React.ReactNode;
}

/** A parent row of a {@link FilterType.TreeMultiSelect} filter together with its selectable children. */
export interface TreeFilterGroup {
  value: string;
  label: string;
  children: OptionType[];
  /** Optional section this group belongs to; groups sharing a section are rendered under one heading. */
  group?: string;
}

export enum DateTimeFormatType {
  DateString,
  ISOString,
}

export interface FilterConfig {
  filterType: FilterType;
  /**
   * Identity of this filter in the bar (chip key, hearted-defaults entry, "add filter" option).
   * Defaults to the first filter key — set it explicitly when two filters edit the *same* keys, as
   * the calendar's "our locations" / "foreign locations" trees do.
   */
  id?: string;
  placeholder?: string;
  /** Short human label for the field, used by the v2 chip bar / "All filters" sidebar. Falls back to `placeholder`. */
  label?: string;
  /**
   * v2 only: force this filter into the chip bar's default set even when it sits beyond the first 7
   * filters (which are seeded as defaults automatically, mirroring the legacy always-visible row).
   * Only seeds a user's initial set; user heart toggles override it afterwards.
   */
  defaultShown?: boolean;
  /**
   * Filter keys this control owns. Most types use a single key; Calendar uses `[from, to]` for a
   * range and {@link FilterType.TreeMultiSelect} uses `[parentKey, childKey]`.
   */
  value: string[];
  options?: OptionType[];
  /** {@link FilterType.TreeMultiSelect} only — the parent rows and their children. */
  treeOptions?: TreeFilterGroup[];
  onChange?: (value: string) => void;
  dateTimeFormat?: DateTimeFormatType;
  onLazyLoad?: (event: { first: number; last: number; filter?: string }) => void;
  lazyLoading?: boolean;
  dataTestId?: string;
}

/**
 * Every filter key the bar can set, in declaration order. This is a table's complete filter vocabulary:
 * anything outside it in the URL belongs to the page or to another table.
 */
export const getFilterFields = (filtersConf: FilterConfig[] | undefined): string[] => [...new Set((filtersConf ?? []).flatMap(c => c.value))];

/**
 * Filter keys whose value is always an array, so `useFilter` can coerce a single URL value
 * (`?rampIds=abc`) back into a one-element array instead of a bare string.
 */
export const getArrayFilterFields = (filtersConf: FilterConfig[] | undefined): string[] => [
  ...new Set(
    (filtersConf ?? [])
      .filter(c => c.filterType === FilterType.MultiSelect || c.filterType === FilterType.MultiSelectLazy || c.filterType === FilterType.TreeMultiSelect)
      .flatMap(c => c.value),
  ),
];

export interface SortableColumn {
  field: string;
  label: string;
}

/** A filter the user stored on the backend, re-appliable from the saved-filter pills. */
export interface SavedFilter<TFilter> {
  id: string;
  name: string;
  data: TFilter;
  sort: { sortField: string; sortDirection: 'ASC' | 'DESC' };
}

/**
 * Shared prop contract for both filter-bar implementations (legacy grid + new v2 chip bar),
 * so RingilFilterBar can swap them with an identical prop payload.
 */
export interface FilterBarProps<TFilter> {
  filtersConf?: FilterConfig[];
  /** Filtered-view identity — used by the v2 bar to scope per-user hearted (default) filter fields. */
  filterName: string;
  filter: TFilter;
  applyFilterValue: (property: string, value: any) => void;
  applyFilter: (filter: TFilter) => void;
  /** Omitted by views without a saved-filter API — no pills are rendered then. */
  savedFilters?: SavedFilter<TFilter>[];
  /** Whether the "Save" control is available (mirrors the presence of getSavedFilters). */
  savingEnabled: boolean;
  loading: boolean;
  isMobile?: boolean;
  /** Legacy bar only — the v2 chip bar has no "show more filters" expander. */
  expandedFilters?: boolean;
  setExpandedFilters?: (value: boolean) => void;
  onSaveFilter: () => void;
  onClearFilter: () => void;
  onManageFilters: () => void;
  /** Current sort state — consumed by the v2 sort chip; ignored by the legacy bar. */
  sort?: FilterSort;
  /** Apply a new sort — consumed by the v2 sort chip; ignored by the legacy bar. */
  applySorting?: (sortField: string, sortDirection: 'ASC' | 'DESC') => void;
  /** Sortable fields (field + translated label) derived from the view's columns. */
  sortableColumns?: SortableColumn[];
  /**
   * `compact`: the RingilDataTable v3 look — no box behind the bar and a labelled "All filters" button
   * (Figma 21940:60909). Defaults to today's look; the legacy bar ignores it.
   */
  variant?: 'default' | 'compact';
  /**
   * Opens the caller's own "All filters" sidebar instead of the bar's (RingilDataTable v3 shows one
   * sidebar for this button and for its tab menu). The bar then renders no sidebar of its own.
   */
  onOpenAllFilters?: () => void;
}

/**
 * Returns true when the given filter object has at least one populated, user-meaningful value.
 * `runAs` / `openMessages` are technical params and are intentionally ignored.
 */
export const isFilterObjectActive = <TFilter>(filterToCheck: TFilter | undefined | null): boolean => {
  if (!filterToCheck) {
    return false;
  }

  const keys = Object.keys(filterToCheck);

  if (keys.length > 0) {
    const hasPopulatedValue = keys.some(key => {
      const value = filterToCheck[key as keyof TFilter];
      if (key === 'runAs' || key === 'openMessages') {
        return false;
      } else if (Array.isArray(value)) {
        if (Array.isArray(value) && value.length > 0) return true;
      } else if (typeof value === 'boolean') {
        if (value === true) return true;
      } else if (!isNullOrUndefined(value)) {
        return !(typeof value === 'string' && isBlank(value));
      } else {
        return false;
      }
    });

    if (hasPopulatedValue) {
      return true;
    }
  }

  return false;
};
